import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Modal } from "antd";
import "./replayVideoDownload.css";

// «Descargar partido»: crea un MP4 de la repetición a partir de los datos de la
// partida (no graba la pantalla). Usuarios: en su computadora. Administradores:
// eligen su computadora o el servidor.
const SCRIPT_SOURCES = ["/replay-video/mp4-muxer.js?v=11", "/replay-video/exporter.js?v=11"];
const loadedScripts = new Map();

const loadScript = (src) => {
  if (loadedScripts.has(src)) return loadedScripts.get(src);
  const promise = new Promise((resolve, reject) => {
    const element = document.createElement("script");
    element.src = src;
    element.async = true;
    element.onload = () => resolve();
    element.onerror = () => {
      loadedScripts.delete(src);
      reject(new Error("No se pudo cargar el creador de videos."));
    };
    document.head.appendChild(element);
  });
  loadedScripts.set(src, promise);
  return promise;
};

const FORMAT_OPTIONS = [
  { id: "landscape", label: "Horizontal", ratio: "16:9", size: "1920 × 1080", hint: "YouTube, ordenador, TV" },
  { id: "portrait", label: "Vertical", ratio: "9:16", size: "1080 × 1920", hint: "TikTok, Reels, Shorts" },
];

// Encuadre del tablero elegido por la persona (por formato, se recuerda en este navegador).
// scale 1 y x/y 0 = la distribución de siempre. x/y en fracción del ancho/alto del video.
const CHOOSER_WIDTH = 480;
const FRAMING_STORAGE_KEY = "xima_video_framing_v1";
const DEFAULT_FRAMING = { scale: 1, x: 0, y: 0, labels: {}, dice: null };
const FRAMING_MIN_SCALE = 0.4;
const FRAMING_MAX_SCALE = 2.5;

const clampNumber = (value, min, max, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
};

// Punto en fracción de la pantalla del video (0–1, arriba a la izquierda) o null.
const normalizePoint = (point) => {
  const x = Number(point?.x);
  const y = Number(point?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
};

// Encuadre: tablero (scale, x, y), etiquetas movidas por jugador (labels[0..3]) y dados.
const normalizeFraming = (framing) => {
  const labels = {};
  [0, 1, 2, 3].forEach((slot) => {
    const point = normalizePoint(framing?.labels?.[slot]);
    if (point) labels[slot] = point;
  });
  return {
    scale: clampNumber(framing?.scale, FRAMING_MIN_SCALE, FRAMING_MAX_SCALE, 1),
    x: clampNumber(framing?.x, -0.5, 0.5, 0),
    y: clampNumber(framing?.y, -0.5, 0.5, 0),
    labels,
    dice: normalizePoint(framing?.dice),
  };
};

const loadSavedFraming = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(FRAMING_STORAGE_KEY) || "{}");
    return { landscape: normalizeFraming(saved?.landscape), portrait: normalizeFraming(saved?.portrait) };
  } catch (_error) {
    return { landscape: { ...DEFAULT_FRAMING }, portrait: { ...DEFAULT_FRAMING } };
  }
};

const saveFraming = (value) => {
  try {
    window.localStorage.setItem(FRAMING_STORAGE_KEY, JSON.stringify(value));
  } catch (_error) {
    // Sin almacenamiento: el encuadre vale solo en esta visita.
  }
};

const pointText = (point) => (point ? `${point.x.toFixed(4)},${point.y.toFixed(4)}` : "");

// Igual que videoLayoutPayload de public/replay-video/exporter.js (vista previa = video):
// "1|tamaño|x|y|dados|etiquetas (x,y;x,y;;)|dados (x,y)".
const buildLayoutPayload = (framing, hasDice) => {
  const value = normalizeFraming(framing);
  const labels = [0, 1, 2, 3].map((slot) => pointText(value.labels[slot])).join(";");
  return `1|${value.scale.toFixed(4)}|${value.x.toFixed(4)}|${value.y.toFixed(4)}|${hasDice ? "1" : "0"}|${
    labels.replace(/;+$/, "")
  }|${pointText(value.dice)}`;
};

const isSameFraming = (a, b) => buildLayoutPayload(a, true) === buildLayoutPayload(b, true);

const PHASE_LABELS = {
  review: "El bot está revisando cada jugada…",
  serverLoading: "Preparando el tablero en el servidor (puede tardar unos 10 minutos)…",
  loading: "Cargando el tablero…",
  recording: "Grabando las jugadas…",
  audio: "Mezclando el sonido…",
  encoding: "Preparando el archivo…",
  queued: "Esperando turno en el servidor…",
  rendering: "Creando el video en el servidor…",
  downloading: "Descargando el video…",
  done: "¡Listo!",
};

const slugify = (value) =>
  String(value || "partida")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .toLowerCase() || "partida";

// Teléfonos y tabletas: un segundo tablero (el del video) más la codificación no caben en
// su memoria y el navegador recarga la página. En ellos no se convierte en el dispositivo.
const isPhoneDevice = () => {
  if (typeof navigator === "undefined") return false;
  const agent = String(navigator.userAgent || "");
  return (
    /iPhone|iPad|iPod|Android|Mobile/i.test(agent) ||
    (/Macintosh/i.test(agent) && Number(navigator.maxTouchPoints) > 1)
  );
};

// En el iPhone la descarga directa no siempre funciona: se usa «Compartir» (Guardar video).
const shareOrSaveBlob = async (blob, fileName) => {
  try {
    const file = new File([blob], fileName, { type: "video/mp4" });
    if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
      await navigator.share({ files: [file], title: fileName });
      return;
    }
  } catch (error) {
    if (error && error.name === "AbortError") return;
  }
  saveBlob(blob, fileName);
};

// Chrome y Edge de escritorio: la persona elige dónde guardar el video (File System Access API).
// Así sabe en qué carpeta queda. Otros navegadores y teléfonos: descarga normal (Descargas).
const canPickSaveFile = () =>
  typeof window !== "undefined" && typeof window.showSaveFilePicker === "function" && !isPhoneDevice();

// Devuelve el archivo elegido, null si no se pudo usar o "cancelled" si la persona cerró el selector.
const pickSaveFile = async (fileName) => {
  if (!canPickSaveFile()) return null;
  try {
    return await window.showSaveFilePicker({
      suggestedName: fileName,
      startIn: "videos",
      types: [{ description: "Video MP4", accept: { "video/mp4": [".mp4"] } }],
    });
  } catch (error) {
    if (error && error.name === "AbortError") return "cancelled";
    return null;
  }
};

const writeBlobToHandle = async (handle, blob) => {
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
};

// Ctrl+J abre las descargas del navegador; allí «Mostrar en carpeta» abre la carpeta del PC.
// Una página web no puede abrir carpetas ni chrome://downloads por sí misma.
const isMacDevice = () => typeof navigator !== "undefined" && /Mac/i.test(String(navigator.platform || navigator.userAgent || ""));
const isEdgeBrowser = () => typeof navigator !== "undefined" && /Edg\//.test(String(navigator.userAgent || ""));

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const readErrorMessage = async (error, fallback) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return parsed.message;
    } catch (_error) {
      // Respuesta sin JSON.
    }
  }
  return data?.message || error?.message || fallback;
};

// Rectángulo del área del video dentro del tablero de la repetición (en píxeles del tablero).
// Deja libre la parte que tapa la ventana de «Descargar partido» (si está a un lado).
export const computeVideoPreviewRect = (preview, board) => {
  if (!preview || !board) return null;
  const aspect = preview.format === "portrait" ? 9 / 16 : 16 / 9;
  let from = 0;
  let to = board.width;
  const avoid = preview.avoid;
  if (avoid && avoid.bottom > board.top && avoid.top < board.top + board.height) {
    const boardRight = board.left + board.width;
    if (avoid.left <= board.left + 60 && avoid.right > board.left) {
      from = Math.min(board.width * 0.45, avoid.right - board.left + 12);
    } else if (avoid.right >= boardRight - 60 && avoid.left < boardRight) {
      to = Math.max(board.width * 0.55, avoid.left - board.left - 12);
    }
  }
  const spaceWidth = to - from;
  const width = Math.min(Math.max(40, spaceWidth - 20), Math.max(40, board.height - 72) * aspect);
  const height = width / aspect;
  return {
    left: Math.round(from + (spaceWidth - width) / 2),
    top: Math.round((board.height - height) / 2),
    width: Math.round(width),
    height: Math.round(height),
  };
};

const ReplayVideoDownload = ({
  matchId,
  matchName,
  isAdmin,
  unityUrl,
  canReview = true,
  className = "",
  previewHost = null,
  onPreviewChange = null,
  playbackSpeed = 1,
  onGoPremium = null,
}) => {
  const videoSpeed = Math.max(0.1, Math.min(8, Number(playbackSpeed) || 1));
  const videoSpeedLabel = `${String(Math.round(videoSpeed * 100) / 100).replace(".", ",")}×`;
  const [chooserOpen, setChooserOpen] = useState(false);
  const [framingByFormat, setFramingByFormat] = useState(loadSavedFraming);
  const [hasDice, setHasDice] = useState(true);
  const [dialogOffset, setDialogOffset] = useState({ x: 0, y: 0 });
  const dialogDragRef = useRef(null);
  const dialogMovedRef = useRef(false);
  const frameDragRef = useRef(null);
  const previewFrameRef = useRef(null);
  // Dónde están las etiquetas y los dados en el tablero (lo avisa Unity): asas para moverlos.
  const [videoRects, setVideoRects] = useState({ labels: [], dice: null });
  // Premium: el video solo se puede descargar con Premium (o siendo administrador).
  const [videoAccess, setVideoAccess] = useState(null);
  // Dónde está la ventana (para que el área del video no quede debajo de ella).
  const [dialogBox, setDialogBox] = useState(null);
  const [format, setFormat] = useState("landscape");
  const [where, setWhere] = useState("local");
  const [withReview, setWithReview] = useState(false);
  const onPhone = isPhoneDevice();
  // En el teléfono solo se puede convertir en el servidor (administradores).
  const effectiveWhere = onPhone ? "server" : where;
  const phoneBlocked = onPhone && !isAdmin;
  const [task, setTask] = useState(null);
  const abortRef = useRef(null);
  const serverJobRef = useRef("");
  const pollTimerRef = useRef(null);
  // Archivo elegido con «Guardar como» (Chrome/Edge de escritorio) y video ya guardado.
  const saveHandleRef = useRef(null);
  const savedBlobRef = useRef(null);
  const [folderHelp, setFolderHelp] = useState(false);
  const busy = Boolean(task && !task.finished);

  const stopPolling = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
  };

  useEffect(
    () => () => {
      stopPolling();
      if (abortRef.current) abortRef.current.abort();
      if (serverJobRef.current) {
        axios.delete(`/replays/video-jobs/${serverJobRef.current}`).catch(() => {});
      }
    },
    []
  );

  const buildFileName = useCallback(
    (selectedFormat) =>
      `xima-${slugify(matchName)}-${selectedFormat === "portrait" ? "vertical" : "horizontal"}.mp4`,
    [matchName]
  );

  const waitForReview = useCallback(
    async (signal) => {
      for (;;) {
        if (signal?.aborted) {
          const abort = new Error("Cancelado.");
          abort.name = "AbortError";
          throw abort;
        }
        const response = await axios.get(`/replays/${matchId}/analysis`);
        const analysis = response?.data?.analysis;
        if (analysis?.state === "done") return Array.isArray(analysis?.result?.moves) ? analysis.result.moves : [];
        if (analysis?.state === "error") throw new Error(analysis.error || "No se pudo revisar la partida.");
        setTask((prev) =>
          prev && !prev.finished
            ? { ...prev, phase: "review", percent: Math.round(Number(analysis?.progress) || 0) }
            : prev
        );
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    },
    [matchId]
  );

  const runLocal = useCallback(
    async (selectedFormat, includeReview, framing, speed) => {
      const controller = new AbortController();
      abortRef.current = controller;
      let chargeId = "";
      setTask({ where: "local", phase: includeReview ? "review" : "loading", percent: 0 });
      try {
        const review = includeReview ? await waitForReview(controller.signal) : null;
        setTask((prev) => (prev && !prev.finished ? { ...prev, phase: "loading", percent: 0 } : prev));
        await Promise.all(SCRIPT_SOURCES.map(loadScript));
        if (!window.XimaReplayVideo || !window.Mp4Muxer) {
          SCRIPT_SOURCES.forEach((src) => loadedScripts.delete(src));
          throw new Error("No se pudo cargar el creador de videos. Recarga la página e inténtalo otra vez.");
        }
        const response = await axios.get(`/replays/${matchId}/video-script`);
        chargeId = String(response?.data?.chargeId || "");
        const script = response?.data?.script;
        if (!script?.steps?.length) throw new Error("Esta partida no tiene jugadas para el video.");
        if (review) script.review = review;
        const result = await window.XimaReplayVideo.exportReplay({
          unityUrl,
          script,
          format: selectedFormat,
          framing,
          speed,
          signal: controller.signal,
          onProgress: (progress) =>
            setTask((prev) =>
              prev && !prev.finished
                ? { ...prev, phase: progress.phase, percent: Math.round(progress.percent) }
                : prev
            ),
        });
        chargeId = "";
        const fileName = buildFileName(selectedFormat);
        const handle = saveHandleRef.current;
        let savedTo = "downloads";
        let savedName = fileName;
        if (handle) {
          try {
            await writeBlobToHandle(handle, result.blob);
            savedTo = "picked";
            savedName = handle.name || fileName;
          } catch (_error) {
            saveBlob(result.blob, fileName);
          }
        } else {
          saveBlob(result.blob, fileName);
        }
        savedBlobRef.current = result.blob;
        setTask({
          where: "local",
          phase: "done",
          percent: 100,
          finished: true,
          notes: result.notes || [],
          sizeMb: result.blob.size / (1024 * 1024),
          savedTo,
          fileName: savedName,
          canOpen: true,
        });
      } catch (error) {
        // El video gratis de hoy no se gasta si la conversión falla o se cancela.
        if (chargeId) axios.post(`/replays/${matchId}/video-refund`, { chargeId }).catch(() => {});
        if (error?.name === "AbortError") {
          setTask(null);
          return;
        }
        setTask({
          where: "local",
          finished: true,
          error: await readErrorMessage(error, "No se pudo crear el video."),
        });
      } finally {
        abortRef.current = null;
      }
    },
    [buildFileName, matchId, unityUrl, waitForReview]
  );

  // Video del servidor: enlace directo de descarga (no se carga en la memoria de la página,
  // así funciona también en el iPhone). El servidor lo borra tras descargarlo.
  const downloadServerFile = useCallback(
    async (jobId, selectedFormat, downloadToken) => {
      serverJobRef.current = "";
      const fileName = buildFileName(selectedFormat);
      const base = String(axios.defaults.baseURL || "/api").replace(/\/+$/, "");
      const absoluteBase = /^https?:/i.test(base) ? base : `${window.location.origin}${base.startsWith("/") ? "" : "/"}${base}`;
      const downloadUrl = `${absoluteBase}/replays/video-jobs/${encodeURIComponent(jobId)}/download?t=${encodeURIComponent(downloadToken || "")}`;
      const handle = saveHandleRef.current;
      if (handle && !isPhoneDevice()) {
        try {
          setTask((prev) => (prev && !prev.finished ? { ...prev, phase: "downloading", percent: 0 } : prev));
          const response = await fetch(downloadUrl);
          if (!response.ok || !response.body) throw new Error("download_failed");
          const total = Number(response.headers.get("content-length")) || 0;
          const writable = await handle.createWritable();
          const reader = response.body.getReader();
          let received = 0;
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            received += value.length;
            await writable.write(value);
            if (total) {
              const downloaded = Math.round((received / total) * 100);
              setTask((prev) => (prev && !prev.finished ? { ...prev, phase: "downloading", percent: downloaded } : prev));
            }
          }
          await writable.close();
          setTask({
            where: "server",
            phase: "done",
            percent: 100,
            finished: true,
            notes: [],
            sizeMb: received / (1024 * 1024),
            savedTo: "picked",
            fileName: handle.name || fileName,
            canOpen: true,
          });
          return;
        } catch (_error) {
          // Sigue con la descarga normal del navegador.
        }
      }
      if (!isPhoneDevice()) {
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTask({
        where: "server",
        phase: "done",
        percent: 100,
        finished: true,
        notes: [],
        downloadUrl: isPhoneDevice() ? downloadUrl : "",
        fileName,
        savedTo: isPhoneDevice() ? "" : "downloads",
      });
    },
    [buildFileName]
  );

  const runServer = useCallback(
    async (selectedFormat, includeReview, framing, speed) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setTask({ where: "server", phase: includeReview ? "review" : "queued", percent: 0 });
      try {
        if (includeReview) await waitForReview(controller.signal);
        abortRef.current = null;
        setTask((prev) => (prev && !prev.finished ? { ...prev, phase: "queued", percent: 0 } : prev));
        const response = await axios.post(`/replays/${matchId}/video-render`, {
          format: selectedFormat,
          withReview: Boolean(includeReview),
          framing,
          speed,
        });
        const jobId = response?.data?.job?.id;
        if (!jobId) throw new Error("El servidor no empezó el video.");
        serverJobRef.current = jobId;
        const startedAt = Date.now();
        const poll = async () => {
          try {
            const status = await axios.get(`/replays/video-jobs/${jobId}`);
            const job = status?.data?.job;
            if (!serverJobRef.current) return;
            if (job?.state === "done") {
              await downloadServerFile(jobId, selectedFormat, job.downloadToken);
              return;
            }
            if (job?.state === "error") {
              serverJobRef.current = "";
              setTask({ where: "server", finished: true, error: job.error || "El servidor no pudo crear el video." });
              return;
            }
            setTask((prev) =>
              prev && !prev.finished
                ? {
                    ...prev,
                    phase: job?.phase === "loading" ? "serverLoading" : job?.phase || "rendering",
                    serverPhase: job?.phase,
                    percent: Math.round(Number(job?.progress) || 0),
                    elapsedSec: Math.round((Date.now() - startedAt) / 1000),
                  }
                : prev
            );
            pollTimerRef.current = setTimeout(poll, 2000);
          } catch (error) {
            serverJobRef.current = "";
            setTask({
              where: "server",
              finished: true,
              error: await readErrorMessage(error, "Se perdió el contacto con el video del servidor."),
            });
          }
        };
        pollTimerRef.current = setTimeout(poll, 1500);
      } catch (error) {
        serverJobRef.current = "";
        if (error?.name === "AbortError") {
          setTask(null);
          return;
        }
        setTask({
          where: "server",
          finished: true,
          error: await readErrorMessage(error, "No se pudo empezar el video en el servidor."),
        });
      }
    },
    [downloadServerFile, matchId, waitForReview]
  );

  const handleStart = async () => {
    if (videoBlocked) return;
    if (phoneBlocked) return;
    const includeReview = canReview && withReview;
    const chosenFraming = normalizeFraming(framingByFormat[format]);
    savedBlobRef.current = null;
    setFolderHelp(false);
    // Se pide aquí (necesita el clic). Si cierra el selector, el video va a Descargas.
    const picked = await pickSaveFile(buildFileName(format));
    saveHandleRef.current = picked && picked !== "cancelled" ? picked : null;
    setChooserOpen(false);
    if (isAdmin && effectiveWhere === "server") runServer(format, includeReview, chosenFraming, videoSpeed);
    else runLocal(format, includeReview, chosenFraming, videoSpeed);
  };

  // ---- Encuadre del tablero (vista previa sobre la repetición) ----
  const framing = normalizeFraming(framingByFormat[format]);
  const updateFraming = useCallback(
    (updater) => {
      setFramingByFormat((prev) => {
        const current = normalizeFraming(prev[format]);
        const next = {
          ...prev,
          [format]: normalizeFraming(typeof updater === "function" ? updater(current) : updater),
        };
        saveFraming(next);
        return next;
      });
    },
    [format]
  );
  const zoomBy = useCallback(
    (factor) => updateFraming((current) => ({ ...current, scale: current.scale * factor })),
    [updateFraming]
  );

  // Al abrir, la ventana se pone a la izquierda para no tapar el área del video
  // (salvo que la persona ya la haya movido a otro sitio).
  useEffect(() => {
    if (!chooserOpen || dialogMovedRef.current || onPhone) return;
    const width = Math.min(CHOOSER_WIDTH, window.innerWidth - 32);
    setDialogOffset({ x: -Math.max(0, window.innerWidth / 2 - width / 2 - 16), y: 0 });
  }, [chooserOpen, onPhone]);

  useEffect(() => {
    if (!chooserOpen) return undefined;
    let cancelled = false;
    axios
      .get("/replays/access")
      .then((response) => {
        if (cancelled) return;
        setVideoAccess({
          premium: Boolean(response?.data?.access?.premium),
          videosRemaining: Math.max(0, Number(response?.data?.access?.videosRemaining) || 0),
          message: String(response?.data?.videoMessage || ""),
        });
      })
      .catch(() => {
        if (!cancelled) setVideoAccess({ premium: true, message: "" });
      });
    return () => {
      cancelled = true;
    };
  }, [chooserOpen]);
  // Sin Premium: 1 video gratis al día (solo «Convertir en mi computadora»).
  const videoBlocked = Boolean(videoAccess && !videoAccess.premium && videoAccess.videosRemaining <= 0);
  const videoFreeLeft = Boolean(videoAccess && !videoAccess.premium && videoAccess.videosRemaining > 0);

  // ¿La partida tiene dados? (en vertical, sin dados las etiquetas van juntas en el centro).
  useEffect(() => {
    if (!chooserOpen || !matchId) return undefined;
    let cancelled = false;
    axios
      .get(`/replays/${matchId}/video-info`)
      .then((response) => {
        if (!cancelled) setHasDice(response?.data?.hasDice !== false);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [chooserOpen, matchId]);

  // Medida de la ventana al abrirla y al terminar de moverla.
  const measureDialog = useCallback(() => {
    const element = document.querySelector(".rv-modal-chooser .ant-modal-content");
    if (!element) return;
    const rect = element.getBoundingClientRect();
    setDialogBox((prev) => {
      const next = {
        left: Math.round(rect.left),
        right: Math.round(rect.right),
        top: Math.round(rect.top),
        bottom: Math.round(rect.bottom),
      };
      return prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  }, []);
  useEffect(() => {
    if (!chooserOpen) return undefined;
    const timers = [80, 400].map((delay) => setTimeout(measureDialog, delay));
    window.addEventListener("resize", measureDialog);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", measureDialog);
    };
  }, [chooserOpen, dialogOffset, measureDialog]);

  // La repetición muestra el área exacta del video mientras se elige.
  const layoutPayload = buildLayoutPayload(framing, hasDice);
  const dialogBoxKey = dialogBox ? `${dialogBox.left},${dialogBox.right},${dialogBox.top},${dialogBox.bottom}` : "";
  useEffect(() => {
    if (typeof onPreviewChange !== "function") return;
    onPreviewChange(
      chooserOpen && !onPhone && !videoBlocked
        ? { format, payload: layoutPayload, avoid: dialogBoxKey ? dialogBox : null }
        : null
    );
    // dialogBox va por su clave (dialogBoxKey) para no repetir el aviso sin cambios.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chooserOpen, format, layoutPayload, onPhone, onPreviewChange, dialogBoxKey, videoBlocked]);
  useEffect(
    () => () => {
      if (typeof onPreviewChange === "function") onPreviewChange(null);
    },
    [onPreviewChange]
  );

  // Rueda del ratón sobre el área: acercar o alejar el tablero (sin mover la página).
  useEffect(() => {
    const element = previewFrameRef.current;
    if (!element) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      zoomBy(event.deltaY < 0 ? 1.06 : 1 / 1.06);
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  });

  // Unity avisa dónde quedaron las etiquetas y los dados (fracción de la pantalla del video).
  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object" || data.type !== "XIMA_VIDEO_RECTS") return;
      // Los dados solo se ven en su turno: se guarda su último sitio para poder moverlos siempre.
      setVideoRects((prev) => ({
        labels: Array.isArray(data.labels) ? data.labels : [],
        dice: data.dice && typeof data.dice === "object" ? data.dice : prev.dice,
      }));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Arrastrar con el botón izquierdo o con el central (como en el juego):
  //  - sobre el fondo del recuadro mueve el tablero;
  //  - sobre una etiqueta o los dados los mueve a ellos (target = "label:N" o "dice").
  const startFrameDrag = (event, target, startCenter) => {
    if (event.button !== 0 && event.button !== 1) return;
    const frame = previewFrameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    frameDragRef.current = {
      target,
      startX: event.clientX,
      startY: event.clientY,
      width: rect.width,
      height: rect.height,
      start: framing,
      startCenter,
      pointerId: event.pointerId,
    };
    if (frame.setPointerCapture) {
      try {
        frame.setPointerCapture(event.pointerId);
      } catch (_error) {
        // Sin captura el arrastre sigue funcionando mientras el ratón esté encima.
      }
    }
    event.preventDefault();
    event.stopPropagation();
  };
  const handleFramePointerDown = (event) => startFrameDrag(event, "board", null);
  const handleFramePointerMove = (event) => {
    const drag = frameDragRef.current;
    if (!drag || (drag.pointerId != null && event.pointerId !== drag.pointerId)) return;
    const dx = (event.clientX - drag.startX) / Math.max(1, drag.width);
    const dy = (event.clientY - drag.startY) / Math.max(1, drag.height);
    if (drag.target === "board") {
      updateFraming({ ...drag.start, x: drag.start.x + dx, y: drag.start.y + dy });
      return;
    }
    const point = { x: drag.startCenter.x + dx, y: drag.startCenter.y + dy };
    if (drag.target === "dice") {
      updateFraming({ ...drag.start, dice: point });
      return;
    }
    const slot = Number(String(drag.target).split(":")[1]);
    updateFraming({ ...drag.start, labels: { ...drag.start.labels, [slot]: point } });
  };
  const handleFramePointerUp = (event) => {
    frameDragRef.current = null;
    const frame = previewFrameRef.current;
    if (frame && frame.releasePointerCapture) {
      try {
        frame.releasePointerCapture(event.pointerId);
      } catch (_error) {
        // Nada que soltar.
      }
    }
  };
  // El botón central no debe activar el desplazamiento automático del navegador.
  const preventMiddleDefault = (event) => {
    if (event.button === 1) event.preventDefault();
  };
  const rectCenter = (rect) => ({ x: Number(rect.x) + Number(rect.w) / 2, y: Number(rect.y) + Number(rect.h) / 2 });

  // La ventana se arrastra por su título con el botón izquierdo.
  const handleDialogPointerDown = (event) => {
    if (event.button !== 0) return;
    dialogDragRef.current = { startX: event.clientX, startY: event.clientY, start: dialogOffset };
    dialogMovedRef.current = true;
    if (event.currentTarget.setPointerCapture) event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };
  const handleDialogPointerMove = (event) => {
    const drag = dialogDragRef.current;
    if (!drag) return;
    const maxX = window.innerWidth / 2;
    const maxY = window.innerHeight / 2;
    setDialogOffset({
      x: Math.max(-maxX, Math.min(maxX, drag.start.x + event.clientX - drag.startX)),
      y: Math.max(-maxY, Math.min(maxY, drag.start.y + event.clientY - drag.startY)),
    });
  };
  const handleDialogPointerUp = (event) => {
    dialogDragRef.current = null;
    if (event.currentTarget.releasePointerCapture) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const showPreview = Boolean(chooserOpen && !onPhone && previewHost && !videoBlocked);
  // Asa de los dados: donde los dejó la persona (aunque ahora no se vean) o donde los puso Unity.
  const diceHandle =
    videoRects.dice && hasDice
      ? framing.dice
        ? {
            x: framing.dice.x - Number(videoRects.dice.w) / 2,
            y: framing.dice.y - Number(videoRects.dice.h) / 2,
            w: Number(videoRects.dice.w),
            h: Number(videoRects.dice.h),
          }
        : videoRects.dice
      : null;
  const zoomPercent = Math.round(framing.scale * 100);

  const openSavedVideo = async () => {
    try {
      let blob = savedBlobRef.current;
      if (!blob && saveHandleRef.current) blob = await saveHandleRef.current.getFile();
      if (!blob) return;
      const url = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: "video/mp4" }));
      const opened = window.open(url, "_blank");
      if (!opened) {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 10 * 60 * 1000);
    } catch (_error) {
      setFolderHelp(true);
    }
  };

  const closeTask = () => {
    setTask(null);
    setFolderHelp(false);
  };

  const handleCancel = () => {
    stopPolling();
    if (abortRef.current) abortRef.current.abort();
    if (serverJobRef.current) {
      axios.delete(`/replays/video-jobs/${serverJobRef.current}`).catch(() => {});
      serverJobRef.current = "";
    }
    setTask(null);
  };

  const percent = Math.max(0, Math.min(100, Number(task?.percent) || 0));

  return (
    <>
      <button
        type="button"
        className={`rv-download-btn ${className}`}
        onClick={() => setChooserOpen(true)}
        disabled={!matchId || busy}
      >
        <span aria-hidden="true">⬇</span>
        {busy ? `Creando video… ${percent} %` : "Descargar partido"}
      </button>

      {showPreview
        ? createPortal(
            <div
              ref={previewFrameRef}
              className={`rv-frame rv-frame-${format}`}
              onPointerDown={handleFramePointerDown}
              onPointerMove={handleFramePointerMove}
              onPointerUp={handleFramePointerUp}
              onPointerCancel={handleFramePointerUp}
              onMouseDown={preventMiddleDefault}
              onAuxClick={preventMiddleDefault}
              onContextMenu={(event) => event.preventDefault()}
            >
              {videoRects.labels.map((label) => (
                <div
                  key={`rv-label-${label.slot}`}
                  className="rv-handle rv-handle-label"
                  data-handle={`label:${label.slot}`}
                  title="Arrastra para mover esta etiqueta"
                  style={{
                    left: `${Number(label.x) * 100}%`,
                    top: `${Number(label.y) * 100}%`,
                    width: `${Number(label.w) * 100}%`,
                    height: `${Number(label.h) * 100}%`,
                  }}
                  onPointerDown={(event) => startFrameDrag(event, `label:${label.slot}`, rectCenter(label))}
                />
              ))}
              {diceHandle ? (
                <div
                  className="rv-handle rv-handle-dice"
                  data-handle="dice"
                  title="Arrastra para mover los dados"
                  style={{
                    left: `${Number(diceHandle.x) * 100}%`,
                    top: `${Number(diceHandle.y) * 100}%`,
                    width: `${Number(diceHandle.w) * 100}%`,
                    height: `${Number(diceHandle.h) * 100}%`,
                  }}
                  onPointerDown={(event) => startFrameDrag(event, "dice", rectCenter(diceHandle))}
                />
              ) : null}
              <span className="rv-frame-tag">
                Área del video · {format === "portrait" ? "9:16" : "16:9"} · tablero {zoomPercent} %
              </span>
              <span className="rv-frame-hint">
                Arrastra (botón izquierdo o central) el tablero, las etiquetas o los dados · rueda = tamaño
              </span>
            </div>,
            previewHost
          )
        : null}

      <Modal
        open={chooserOpen}
        onCancel={() => setChooserOpen(false)}
        footer={null}
        centered
        width={CHOOSER_WIDTH}
        zIndex={1300}
        mask={false}
        rootClassName="rv-modal rv-modal-chooser"
        modalRender={(node) => (
          <div style={{ transform: `translate(${dialogOffset.x}px, ${dialogOffset.y}px)` }}>{node}</div>
        )}
        title={
          <div
            className="rv-drag-handle"
            onPointerDown={handleDialogPointerDown}
            onPointerMove={handleDialogPointerMove}
            onPointerUp={handleDialogPointerUp}
            onPointerCancel={handleDialogPointerUp}
            title="Arrastra para mover la ventana"
          >
            <span aria-hidden="true">⠿</span> Descargar partido en video (MP4)
          </div>
        }
      >
        <p className="rv-lead">
          El video se crea a partir de las jugadas guardadas, con la cámara siguiendo cada jugada y los
          sonidos del juego.
        </p>
        {videoBlocked ? (
          <div className="rv-premium">
            <strong>⭐ Ya usaste tu video gratis de hoy</strong>
            <p>
              {videoAccess.message ||
                "Descargar más videos es una función Premium: hazte miembro Premium para usarla."}
            </p>
            {typeof onGoPremium === "function" ? (
              <button
                type="button"
                className="rv-premium-btn"
                onClick={() => {
                  setChooserOpen(false);
                  onGoPremium();
                }}
              >
                ⭐ Hazte Premium
              </button>
            ) : null}
          </div>
        ) : videoFreeLeft ? (
          <div className="rv-premium is-free">
            <strong>🎁 Te queda 1 video gratis hoy</strong>
            <p>Con Premium puedes descargar todos los videos que quieras.</p>
            {typeof onGoPremium === "function" ? (
              <button
                type="button"
                className="rv-premium-link"
                onClick={() => {
                  setChooserOpen(false);
                  onGoPremium();
                }}
              >
                Ver Premium →
              </button>
            ) : null}
          </div>
        ) : null}
        <p className="rv-speed">
          Velocidad: <strong>{videoSpeedLabel}</strong>
          <small>
            {videoSpeed < 1
              ? " · más lento que lo normal (se cambia con «Lento» / «Rápido» de la repetición)"
              : videoSpeed > 1
              ? " · más rápido que lo normal (se cambia con «Lento» / «Rápido» de la repetición)"
              : " · normal (se cambia con «Lento» / «Rápido» de la repetición)"}
          </small>
        </p>
        <div className="rv-label">Formato</div>
        <div className="rv-options">
          {FORMAT_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`rv-option${format === option.id ? " is-on" : ""}`}
              onClick={() => setFormat(option.id)}
            >
              <span className={`rv-shape rv-shape-${option.id}`} aria-hidden="true" />
              <strong>
                {option.label} <em>{option.ratio}</em>
              </strong>
              <small>{option.size}</small>
              <small>{option.hint}</small>
            </button>
          ))}
        </div>
        {!onPhone ? (
          <>
            <div className="rv-label">Tamaño y posición del tablero</div>
            <div className="rv-framing">
              <button
                type="button"
                className="rv-zoom-btn"
                onClick={() => zoomBy(1 / 1.1)}
                aria-label="Tablero más pequeño"
              >
                −
              </button>
              <input
                type="range"
                min={FRAMING_MIN_SCALE * 100}
                max={FRAMING_MAX_SCALE * 100}
                step={1}
                value={zoomPercent}
                onChange={(event) =>
                  updateFraming((current) => ({ ...current, scale: Number(event.target.value) / 100 }))
                }
                aria-label="Tamaño del tablero"
              />
              <button type="button" className="rv-zoom-btn" onClick={() => zoomBy(1.1)} aria-label="Tablero más grande">
                +
              </button>
              <strong className="rv-zoom-value">{zoomPercent} %</strong>
              <button
                type="button"
                className="rv-secondary rv-reset"
                onClick={() => updateFraming({ ...DEFAULT_FRAMING })}
                disabled={isSameFraming(framing, DEFAULT_FRAMING)}
              >
                Restablecer
              </button>
            </div>
            <p className="rv-note">
              El recuadro punteado sobre la repetición es el video. Dentro de él arrastra (botón izquierdo o central)
              el tablero, las etiquetas de los jugadores o los dados para colocarlos; la rueda del ratón o estos botones
              cambian el tamaño del tablero. «Restablecer» lo devuelve todo a su sitio. Puedes mover esta ventana
              arrastrando su título.
            </p>
          </>
        ) : null}
        {canReview ? (
          <>
            <div className="rv-label">Tipo de video</div>
            <div className="rv-options">
              <button
                type="button"
                className={`rv-option${withReview ? " is-on" : ""}`}
                onClick={() => setWithReview(true)}
              >
                <span className="rv-icon" aria-hidden="true">★</span>
                <strong>Con revisión del partido</strong>
                <small>Cada bloque lleva su nota (brillante, excelente, error…) y la flecha verde del bot.</small>
              </button>
              <button
                type="button"
                className={`rv-option${!withReview ? " is-on" : ""}`}
                onClick={() => setWithReview(false)}
              >
                <span className="rv-icon" aria-hidden="true">▶</span>
                <strong>Normal, sin revisión</strong>
                <small>La partida tal como se jugó.</small>
              </button>
            </div>
          </>
        ) : null}
        {phoneBlocked ? (
          <p className="rv-error">En este teléfono no se puede convertir el video; usa una computadora.</p>
        ) : null}
        {isAdmin && onPhone ? (
          <p className="rv-note">
            En el teléfono el video se convierte en el servidor (el teléfono no tiene memoria suficiente).
          </p>
        ) : null}
        {isAdmin && !onPhone ? (
          <>
            <div className="rv-label">¿Dónde se convierte?</div>
            <div className="rv-options">
              <button
                type="button"
                className={`rv-option${where === "local" ? " is-on" : ""}`}
                onClick={() => setWhere("local")}
              >
                <span className="rv-icon" aria-hidden="true">💻</span>
                <strong>Convertir en mi computadora</strong>
                <small>Usa los recursos de este equipo. Deja la pestaña abierta.</small>
              </button>
              <button
                type="button"
                className={`rv-option${where === "server" ? " is-on" : ""}`}
                onClick={() => setWhere("server")}
              >
                <span className="rv-icon" aria-hidden="true">🖥️</span>
                <strong>Convertir en el servidor</strong>
                <small>Usa los recursos del servidor. El archivo se borra al descargarlo.</small>
              </button>
            </div>
          </>
        ) : null}
        <div className="rv-actions">
          <button type="button" className="rv-secondary" onClick={() => setChooserOpen(false)}>
            Cancelar
          </button>
          <button type="button" className="rv-primary" onClick={handleStart} disabled={phoneBlocked || videoBlocked}>
            Crear video
          </button>
        </div>
      </Modal>

      <Modal
        open={Boolean(task)}
        onCancel={task?.finished ? closeTask : undefined}
        closable={Boolean(task?.finished)}
        maskClosable={false}
        footer={null}
        centered
        width={480}
        zIndex={1300}
        rootClassName="rv-modal"
        title={task?.error ? "No se pudo crear el video" : task?.finished ? "Video listo" : "Creando video…"}
      >
        {task?.error ? (
          <>
            <p className="rv-error">{task.error}</p>
            <div className="rv-actions">
              <button type="button" className="rv-primary" onClick={() => setTask(null)}>
                Cerrar
              </button>
            </div>
          </>
        ) : task?.finished ? (
          <>
            {task.downloadUrl ? (
              <p className="rv-lead">
                El video está listo. Toca «Guardar video»: se abre el video y desde ahí puedes guardarlo
                (botón Compartir → «Guardar video»). En Android queda en la carpeta Descargas (app Archivos);
                en iPhone, en Fotos o en Archivos.
              </p>
            ) : task.blob ? (
              <p className="rv-lead">
                El video está listo{task.sizeMb >= 0.1 ? ` (${task.sizeMb.toFixed(1)} MB)` : ""}. Toca «Guardar video»
                y elige «Guardar video» o la app donde quieras guardarlo.
              </p>
            ) : task.savedTo === "picked" ? (
              <p className="rv-lead">
                Video guardado{task.sizeMb >= 0.1 ? ` (${task.sizeMb.toFixed(1)} MB)` : ""} en la carpeta que elegiste:
                <span className="rv-file">{task.fileName}</span>
              </p>
            ) : (
              <p className="rv-lead">
                Video guardado en tu carpeta de Descargas{task.sizeMb >= 0.1 ? ` (${task.sizeMb.toFixed(1)} MB)` : ""}:
                <span className="rv-file">{task.fileName}</span>
              </p>
            )}
            {folderHelp && task.savedTo ? (
              <div className="rv-folder-help" role="note">
                {task.savedTo === "picked" ? (
                  <>
                    El navegador no deja que una página abra carpetas de tu PC. Abre el Explorador de archivos
                    (<kbd>Windows</kbd>+<kbd>E</kbd>) y entra en la carpeta que elegiste (si no la cambiaste, «Videos»).
                  </>
                ) : (
                  <>
                    Pulsa <kbd>{isMacDevice() ? "⌘" : "Ctrl"}</kbd>+{isMacDevice() ? <><kbd>Mayús</kbd>+</> : null}<kbd>J</kbd>{" "}
                    (descargas de {isEdgeBrowser() ? "Edge" : "tu navegador"}) y, en este video, elige «Mostrar en carpeta».
                  </>
                )}
              </div>
            ) : null}
            {(task.notes || []).map((note) => (
              <p key={note} className="rv-note">
                {note}
              </p>
            ))}
            <div className="rv-actions">
              {task.downloadUrl ? (
                <a className="rv-primary rv-link" href={task.downloadUrl} target="_blank" rel="noopener noreferrer" download={task.fileName}>
                  Guardar video
                </a>
              ) : task.blob ? (
                <button
                  type="button"
                  className="rv-primary"
                  onClick={() => shareOrSaveBlob(task.blob, task.fileName || "partida-xima.mp4")}
                >
                  Guardar video
                </button>
              ) : null}
              {task.savedTo && task.canOpen ? (
                <button type="button" className="rv-secondary" onClick={openSavedVideo}>
                  Abrir video
                </button>
              ) : null}
              {task.savedTo ? (
                <button type="button" className="rv-primary" onClick={() => setFolderHelp(true)}>
                  Abrir en carpeta
                </button>
              ) : null}
              <button
                type="button"
                className={task.blob || task.downloadUrl || task.savedTo ? "rv-secondary" : "rv-primary"}
                onClick={closeTask}
              >
                Cerrar
              </button>
            </div>
          </>
        ) : task ? (
          <>
            <div className="rv-progress-head">
              <span>{PHASE_LABELS[task.phase] || "Creando video…"}</span>
              <strong>{percent} %</strong>
            </div>
            <div className="rv-progress">
              <div style={{ width: `${percent}%` }} />
            </div>
            <p className="rv-note">
              {task.where === "server"
                ? `Se está creando en el servidor${task.elapsedSec ? ` · ${Math.floor(task.elapsedSec / 60)} min ${task.elapsedSec % 60} s` : ""}. Puedes seguir viendo la repetición.`
                : "Se está creando en tu computadora. Deja esta pestaña abierta; puedes seguir viendo la repetición."}
            </p>
            <div className="rv-actions">
              <button type="button" className="rv-secondary" onClick={handleCancel}>
                Cancelar
              </button>
            </div>
          </>
        ) : null}
      </Modal>
    </>
  );
};

export default ReplayVideoDownload;
